import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import ERPLayout from "@/components/feature/ERPLayout";
import { paymentsApi } from "@/services/api";

export interface Payment {
  id: number;
  paymentRef: string;
  invoiceId: number;
  invoiceNo: string;
  clientName: string;
  clientCompany: string;
  amount: number;
  paymentDate: string;
  paymentMode: string;
  bankName: string;
  utrChequeNo: string;
  status: "received" | "pending" | "failed" | "refunded";
  notes: string;
  createdAt: string;
}

const STATUS_META: Record<string, { label: string; bg: string; text: string; icon: string }> = {
  received: { label: "Received",  bg: "#f0fdf4", text: "#16a34a", icon: "ri-checkbox-circle-line" },
  pending:  { label: "Pending",   bg: "#fffbeb", text: "#d97706", icon: "ri-time-line" },
  failed:   { label: "Failed",    bg: "#fef2f2", text: "#dc2626", icon: "ri-close-circle-line" },
  refunded: { label: "Refunded",  bg: "#f5f3ff", text: "#7c3aed", icon: "ri-arrow-go-back-line" },
};

const PAYMENT_MODES = ["NEFT", "RTGS", "UPI", "Cheque", "Cash", "IMPS", "DD"];

interface AddPaymentModalProps {
  onClose: () => void;
  onSave: (data: Partial<Payment>) => void;
}

function AddPaymentModal({ onClose, onSave }: AddPaymentModalProps) {
  const [form, setForm] = useState<Partial<Payment>>({
    invoiceNo: "",
    clientName: "",
    clientCompany: "",
    amount: 0,
    paymentDate: new Date().toISOString().split("T")[0],
    paymentMode: "NEFT",
    bankName: "",
    utrChequeNo: "",
    status: "received",
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!form.invoiceNo || !form.clientName || !form.amount) return;
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
              <i className="ri-money-rupee-circle-line text-xl text-amber-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">Record Payment</h3>
              <p className="text-xs text-gray-500 mt-0.5">Add a new payment entry</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4 max-h-[60vh] overflow-y-auto">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1.5 block">Invoice No. *</label>
              <input
                type="text"
                value={form.invoiceNo || ""}
                onChange={(e) => setForm({ ...form, invoiceNo: e.target.value })}
                placeholder="INV-2026-XXXX"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1.5 block">Amount (₹) *</label>
              <input
                type="number"
                value={form.amount || ""}
                onChange={(e) => setForm({ ...form, amount: Number(e.target.value) })}
                placeholder="0"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1.5 block">Client Name *</label>
              <input
                type="text"
                value={form.clientName || ""}
                onChange={(e) => setForm({ ...form, clientName: e.target.value })}
                placeholder="Client name"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1.5 block">Company</label>
              <input
                type="text"
                value={form.clientCompany || ""}
                onChange={(e) => setForm({ ...form, clientCompany: e.target.value })}
                placeholder="Company name"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1.5 block">Payment Date *</label>
              <input
                type="date"
                value={form.paymentDate || ""}
                onChange={(e) => setForm({ ...form, paymentDate: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1.5 block">Payment Mode</label>
              <select
                value={form.paymentMode || "NEFT"}
                onChange={(e) => setForm({ ...form, paymentMode: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30"
              >
                {PAYMENT_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1.5 block">Bank Name</label>
              <input
                type="text"
                value={form.bankName || ""}
                onChange={(e) => setForm({ ...form, bankName: e.target.value })}
                placeholder="e.g. State Bank of India"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1.5 block">UTR / Cheque No.</label>
              <input
                type="text"
                value={form.utrChequeNo || ""}
                onChange={(e) => setForm({ ...form, utrChequeNo: e.target.value })}
                placeholder="Transaction reference"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30"
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-600 mb-1.5 block">Status</label>
            <div className="flex gap-2 flex-wrap">
              {(["received", "pending", "failed", "refunded"] as const).map((s) => {
                const meta = STATUS_META[s];
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setForm({ ...form, status: s })}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap border-2 ${form.status === s ? "border-current" : "border-transparent"}`}
                    style={{ background: meta.bg, color: meta.text }}
                  >
                    <i className={`${meta.icon} mr-1`} />{meta.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-600 mb-1.5 block">Notes</label>
            <textarea
              value={form.notes || ""}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Optional notes about this payment..."
              rows={2}
              maxLength={500}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 resize-none"
            />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
          <button onClick={onClose} className="flex-1 py-2.5 border border-gray-200 text-gray-600 rounded-xl text-sm font-semibold cursor-pointer hover:bg-gray-50 transition-all whitespace-nowrap">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving || !form.invoiceNo || !form.clientName || !form.amount}
            className="flex-1 py-2.5 bg-amber-600 text-white rounded-xl text-sm font-bold cursor-pointer hover:bg-amber-700 transition-all whitespace-nowrap disabled:opacity-60"
          >
            {saving ? <><i className="ri-loader-4-line animate-spin mr-1" />Saving...</> : <><i className="ri-save-line mr-1" />Save Payment</>}
          </button>
        </div>
      </div>
    </div>
  );
}

interface EditPaymentModalProps {
  payment: Payment;
  onClose: () => void;
  onSave: (id: number, data: Partial<Payment>) => void;
}

function EditPaymentModal({ payment, onClose, onSave }: EditPaymentModalProps) {
  const [form, setForm] = useState<Partial<Payment>>({ ...payment });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    setSaving(true);
    await onSave(payment.id, form);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
              <i className="ri-edit-line text-xl text-amber-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">Edit Payment</h3>
              <p className="text-xs text-gray-500 mt-0.5">{payment.paymentRef}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4 max-h-[60vh] overflow-y-auto">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1.5 block">Payment Date</label>
              <input
                type="date"
                value={form.paymentDate || ""}
                onChange={(e) => setForm({ ...form, paymentDate: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1.5 block">Amount (₹)</label>
              <input
                type="number"
                value={form.amount || ""}
                onChange={(e) => setForm({ ...form, amount: Number(e.target.value) })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1.5 block">Payment Mode</label>
              <select
                value={form.paymentMode || "NEFT"}
                onChange={(e) => setForm({ ...form, paymentMode: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30"
              >
                {PAYMENT_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1.5 block">UTR / Cheque No.</label>
              <input
                type="text"
                value={form.utrChequeNo || ""}
                onChange={(e) => setForm({ ...form, utrChequeNo: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30"
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-600 mb-1.5 block">Status</label>
            <div className="flex gap-2 flex-wrap">
              {(["received", "pending", "failed", "refunded"] as const).map((s) => {
                const meta = STATUS_META[s];
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setForm({ ...form, status: s })}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap border-2 ${form.status === s ? "border-current" : "border-transparent"}`}
                    style={{ background: meta.bg, color: meta.text }}
                  >
                    <i className={`${meta.icon} mr-1`} />{meta.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-600 mb-1.5 block">Notes</label>
            <textarea
              value={form.notes || ""}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={2}
              maxLength={500}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 resize-none"
            />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
          <button onClick={onClose} className="flex-1 py-2.5 border border-gray-200 text-gray-600 rounded-xl text-sm font-semibold cursor-pointer hover:bg-gray-50 transition-all whitespace-nowrap">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="flex-1 py-2.5 bg-amber-600 text-white rounded-xl text-sm font-bold cursor-pointer hover:bg-amber-700 transition-all whitespace-nowrap disabled:opacity-60"
          >
            {saving ? <><i className="ri-loader-4-line animate-spin mr-1" />Saving...</> : <><i className="ri-save-line mr-1" />Update Payment</>}
          </button>
        </div>
      </div>
    </div>
  );
}

interface DeleteModalProps {
  payment: Payment;
  onConfirm: () => void;
  onCancel: () => void;
}

function DeleteModal({ payment, onConfirm, onCancel }: DeleteModalProps) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-sm overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
            <i className="ri-delete-bin-line text-xl text-red-500" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900">Delete Payment</h3>
            <p className="text-xs text-gray-500 mt-0.5">{payment.paymentRef}</p>
          </div>
        </div>
        <div className="px-6 py-5">
          <p className="text-sm text-gray-600 leading-relaxed">
            Are you sure you want to delete payment <strong>{payment.paymentRef}</strong> of{" "}
            <strong>₹{payment.amount.toLocaleString("en-IN")}</strong>? This action cannot be undone.
          </p>
        </div>
        <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
          <button onClick={onCancel} className="flex-1 py-2.5 border border-gray-200 text-gray-600 rounded-xl text-sm font-semibold cursor-pointer hover:bg-gray-50 transition-all whitespace-nowrap">
            Cancel
          </button>
          <button onClick={onConfirm} className="flex-1 py-2.5 bg-red-500 text-white rounded-xl text-sm font-bold cursor-pointer hover:bg-red-600 transition-all whitespace-nowrap">
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PaymentsPage() {
  const navigate = useNavigate();
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [modeFilter, setModeFilter] = useState<string>("all");
  const [showAdd, setShowAdd] = useState(false);
  const [editTarget, setEditTarget] = useState<Payment | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Payment | null>(null);
  const [toast, setToast] = useState("");

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3000);
  };

  /** Normalize ISO datetime strings to yyyy-MM-dd for date inputs */
  const normPaymentDate = (p: Payment): Payment => ({
    ...p,
    paymentDate: p.paymentDate
      ? (p.paymentDate.match(/^(\d{4}-\d{2}-\d{2})/)?.[1] ?? p.paymentDate)
      : "",
  });

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (search.trim()) params.search = search.trim();
      const data = await paymentsApi.getAll(params);
      const raw: Payment[] = Array.isArray(data) ? data : data.data ?? [];
      setPayments(raw.map(normPaymentDate));
    } catch {
      setPayments([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search]);

  useEffect(() => {
    const timer = setTimeout(fetchPayments, 300);
    return () => clearTimeout(timer);
  }, [fetchPayments]);

  const handleAdd = async (data: Partial<Payment>) => {
    const now = new Date();
    const newRef = `PAY-${now.getFullYear()}-${String(Math.floor(Math.random() * 9000) + 1000)}`;
    const newPayment: Payment = {
      id: Date.now(),
      paymentRef: newRef,
      invoiceId: 0,
      createdAt: now.toISOString(),
      ...data,
    } as Payment;
    try {
      await paymentsApi.create(data as Record<string, unknown>);
    } catch {
      // Optimistic
    }
    setPayments((prev) => [newPayment, ...prev]);
    setShowAdd(false);
    showToast(`Payment ${newRef} recorded successfully`);
  };

  const handleEdit = async (id: number, data: Partial<Payment>) => {
    try {
      await paymentsApi.update(id, data as Record<string, unknown>);
    } catch {
      // Optimistic
    }
    setPayments((prev) => prev.map((p) => p.id === id ? { ...p, ...data } : p));
    setEditTarget(null);
    showToast("Payment updated successfully");
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await paymentsApi.delete(deleteTarget.id);
    } catch {
      // Optimistic
    }
    setPayments((prev) => prev.filter((p) => p.id !== deleteTarget.id));
    showToast(`${deleteTarget.paymentRef} deleted`);
    setDeleteTarget(null);
  };

  const filtered = modeFilter === "all" ? payments : payments.filter((p) => p.paymentMode === modeFilter);

  const totalReceived = payments.filter((p) => p.status === "received").reduce((a, b) => a + b.amount, 0);
  const totalPending = payments.filter((p) => p.status === "pending").reduce((a, b) => a + b.amount, 0);
  const totalFailed = payments.filter((p) => p.status === "failed").reduce((a, b) => a + b.amount, 0);
  const totalRefunded = payments.filter((p) => p.status === "refunded").reduce((a, b) => a + b.amount, 0);

  const modeBreakdown = PAYMENT_MODES.reduce((acc, mode) => {
    const count = payments.filter((p) => p.paymentMode === mode && p.status === "received").length;
    const amount = payments.filter((p) => p.paymentMode === mode && p.status === "received").reduce((a, b) => a + b.amount, 0);
    if (count > 0) acc[mode] = { count, amount };
    return acc;
  }, {} as Record<string, { count: number; amount: number }>);

  return (
    <ERPLayout title="Payments">
      {toast && (
        <div className="fixed top-4 right-4 z-50 flex items-center gap-2 bg-gray-900 text-white text-sm px-4 py-2.5 rounded-xl font-medium">
          <i className="ri-checkbox-circle-line text-amber-400" />{toast}
        </div>
      )}

      {showAdd && <AddPaymentModal onClose={() => setShowAdd(false)} onSave={handleAdd} />}
      {editTarget && <EditPaymentModal payment={editTarget} onClose={() => setEditTarget(null)} onSave={handleEdit} />}
      {deleteTarget && <DeleteModal payment={deleteTarget} onConfirm={handleDelete} onCancel={() => setDeleteTarget(null)} />}

      <div className="p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <button onClick={() => navigate("/billing")} className="text-gray-400 hover:text-gray-600 cursor-pointer transition-colors">
                <i className="ri-arrow-left-line text-sm" />
              </button>
              <h2 className="text-xl font-bold text-gray-900">Payments</h2>
            </div>
            <p className="text-sm text-gray-500">
              {loading ? "Loading..." : `${payments.length} payment records — track all received and pending payments`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchPayments}
              className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-refresh-line text-sm" /></span>Refresh
            </button>
            <button
              className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-download-2-line text-sm" /></span>Export
            </button>
            <button
              onClick={() => setShowAdd(true)}
              className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line text-sm" /></span>Record Payment
            </button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {[
            { label: "Total Received",  value: `₹${(totalReceived / 100000).toFixed(1)}L`,  icon: "ri-checkbox-circle-line",    color: "#10b981", bg: "#ecfdf5", count: payments.filter((p) => p.status === "received").length },
            { label: "Pending",         value: `₹${(totalPending / 100000).toFixed(1)}L`,   icon: "ri-time-line",               color: "#d97706", bg: "#fffbeb", count: payments.filter((p) => p.status === "pending").length },
            { label: "Failed",          value: `₹${(totalFailed / 100000).toFixed(1)}L`,    icon: "ri-close-circle-line",       color: "#dc2626", bg: "#fef2f2", count: payments.filter((p) => p.status === "failed").length },
            { label: "Refunded",        value: `₹${(totalRefunded / 100000).toFixed(1)}L`,  icon: "ri-arrow-go-back-line",      color: "#d97706", bg: "#fffbeb", count: payments.filter((p) => p.status === "refunded").length },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-4">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: s.bg }}>
                <i className={`${s.icon} text-lg`} style={{ color: s.color }} />
              </div>
              <div>
                <p className="text-xl font-bold text-gray-900">{s.value}</p>
                <p className="text-xs text-gray-500">{s.label}</p>
                <p className="text-xs text-gray-400">{s.count} transactions</p>
              </div>
            </div>
          ))}
        </div>

        {/* Payment Mode Breakdown */}
        {Object.keys(modeBreakdown).length > 0 && (
          <div className="bg-white rounded-xl border border-gray-100 p-4 mb-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Payment Mode Breakdown (Received)</p>
            <div className="flex flex-wrap gap-3">
              {Object.entries(modeBreakdown).map(([mode, data]) => (
                <div key={mode} className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg border border-gray-100">
                  <span className="w-6 h-6 flex items-center justify-center rounded bg-amber-100 flex-shrink-0">
                    <i className="ri-bank-card-line text-amber-600 text-xs" />
                  </span>
                  <div>
                    <p className="text-xs font-semibold text-gray-800">{mode}</p>
                    <p className="text-xs text-gray-500">{data.count} txn · ₹{(data.amount / 1000).toFixed(0)}K</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-100 p-4 mb-4 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none">
              <i className="ri-search-line text-gray-400 text-sm" />
            </span>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by ref, invoice, client, UTR..."
              className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400"
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {(["all", "received", "pending", "failed", "refunded"] as const).map((s) => {
              const meta = s === "all" ? null : STATUS_META[s];
              return (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${statusFilter === s ? "bg-amber-500 text-white" : "border border-gray-200 text-gray-600 hover:bg-gray-50"}`}
                >
                  {s === "all" ? "All Status" : meta!.label}
                </button>
              );
            })}
            <select
              value={modeFilter}
              onChange={(e) => setModeFilter(e.target.value)}
              className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs text-gray-600 focus:outline-none focus:ring-2 focus:ring-amber-500/30 cursor-pointer"
            >
              <option value="all">All Modes</option>
              {PAYMENT_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <i className="ri-loader-4-line text-3xl text-amber-500 animate-spin" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/60">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">Payment Ref</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">Invoice</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Client</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Amount</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden md:table-cell whitespace-nowrap">Date</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden lg:table-cell whitespace-nowrap">Mode</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden lg:table-cell whitespace-nowrap">UTR / Ref</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={9} className="text-center py-16 text-gray-400">
                        <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 bg-gray-100 rounded-xl">
                          <i className="ri-money-rupee-circle-line text-2xl text-gray-300" />
                        </div>
                        <p className="text-sm font-medium">No payments found</p>
                        <p className="text-xs mt-1">Try adjusting your search or filters</p>
                      </td>
                    </tr>
                  )}
                  {filtered.map((p) => {
                    const meta = STATUS_META[p.status] || STATUS_META.pending;
                    return (
                      <tr key={p.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                        <td className="px-4 py-3">
                          <span className="text-sm font-bold text-gray-800 font-mono">{p.paymentRef}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs font-mono text-gray-600 bg-gray-100 px-2 py-0.5 rounded">{p.invoiceNo}</span>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm font-semibold text-gray-800">{p.clientName}</p>
                          <p className="text-xs text-gray-500">{p.clientCompany}</p>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <p className="text-sm font-bold text-gray-900">₹{p.amount.toLocaleString("en-IN")}</p>
                        </td>
                        <td className="px-4 py-3 text-center hidden md:table-cell">
                          <span className="text-xs text-gray-600">{p.paymentDate}</span>
                        </td>
                        <td className="px-4 py-3 text-center hidden lg:table-cell">
                          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-medium">{p.paymentMode}</span>
                        </td>
                        <td className="px-4 py-3 hidden lg:table-cell">
                          <span className="text-xs font-mono text-gray-500 truncate max-w-[120px] block">{p.utrChequeNo || "—"}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap" style={{ background: meta.bg, color: meta.text }}>
                            <i className={`${meta.icon} mr-1`} />{meta.label}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => setEditTarget(p)}
                              className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-amber-600 hover:bg-amber-50 cursor-pointer transition-all"
                              title="Edit"
                            >
                              <i className="ri-edit-line text-sm" />
                            </button>
                            <button
                              onClick={() => setDeleteTarget(p)}
                              className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 cursor-pointer transition-all"
                              title="Delete"
                            >
                              <i className="ri-delete-bin-line text-sm" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {!loading && (
            <div className="px-4 py-3 border-t border-gray-100 bg-gray-50/50 flex items-center justify-between">
              <p className="text-xs text-gray-500">
                Showing <strong>{filtered.length}</strong> of <strong>{payments.length}</strong> payments
              </p>
              <p className="text-xs text-gray-500">
                Filtered total: <strong className="text-gray-800">₹{filtered.reduce((a, b) => a + b.amount, 0).toLocaleString("en-IN")}</strong>
              </p>
            </div>
          )}
        </div>
      </div>
    </ERPLayout>
  );
}
